/**
 * Cookie Consent Banner
 * =====================
 * Shows a cookie consent notice with link to Privacy Policy.
 * Stores consent in localStorage to not show again.
 */

import React, { useState, useEffect } from 'react';
import { Cookie, X } from 'lucide-react';

const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const hasConsented = localStorage.getItem('nearU_cookieConsent');
    if (!hasConsented) {
      // Small delay before showing to avoid flash on page load
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('nearU_cookieConsent', 'accepted');
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem('nearU_cookieConsent', 'declined');
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      style={{
        position: 'fixed',
        bottom: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 32px)',
        maxWidth: '500px',
        background: 'linear-gradient(135deg, rgba(26, 15, 10, 0.98) 0%, rgba(42, 28, 20, 0.98) 100%)',
        backdropFilter: 'blur(20px)',
        borderRadius: '20px',
        padding: '20px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(215, 181, 109, 0.2)',
        zIndex: 9999,
        animation: 'slideUp 0.4s ease-out'
      }}
    >
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>

      {/* Close button */}
      <button
        onClick={handleDecline}
        style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          background: 'none',
          border: 'none',
          color: '#8A7355',
          cursor: 'pointer',
          padding: '4px'
        }}
        aria-label="Close"
      >
        <X size={18} />
      </button>

      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
        {/* Cookie Icon */}
        <div style={{
          width: '44px',
          height: '44px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.2) 0%, rgba(165, 124, 51, 0.1) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <Cookie size={22} color="#D7B56D" />
        </div>

        {/* Content */}
        <div style={{ flex: 1 }}>
          <h3 style={{
            fontFamily: 'Cinzel, serif',
            fontSize: '16px',
            fontWeight: '600',
            color: '#E6D7C3',
            margin: '0 0 8px 0'
          }}>
            Cookie Notice
          </h3>
          <p style={{
            color: '#A57C33',
            fontSize: '13px',
            lineHeight: '1.6',
            margin: '0 0 16px 0'
          }}>
            We use cookies to enhance your experience. By continuing to use NearU, you agree to our{' '}
            <a
              href="/privacy"
              style={{
                color: '#D7B56D',
                textDecoration: 'underline'
              }}
            >
              Privacy Policy
            </a>
            .
          </p>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleAccept}
              data-testid="cookie-accept-btn"
              style={{
                flex: 1,
                padding: '12px 20px',
                background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                border: 'none',
                borderRadius: '10px',
                color: '#1A0F0A',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Accept
            </button>
            <button
              onClick={handleDecline}
              data-testid="cookie-decline-btn"
              style={{
                flex: 1,
                padding: '12px 20px',
                background: 'transparent',
                border: '1px solid rgba(165, 124, 51, 0.3)',
                borderRadius: '10px',
                color: '#A57C33',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
